import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'darkMode';
// Fired on window whenever the theme changes, so every place that shows the toggle stays in step.
// The Topbar is still mounted (only hidden) under the mobile profile bar, so without this its icon
// would go stale after a toggle in Settings and be wrong once the window is wide again.
const CHANGE_EVENT = 'golfplayed:dark-mode';

const readStored = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const apply = (isDark: boolean): void => {
  document.documentElement.classList.toggle('dark', isDark);
};

// The user's own theme choice, kept in localStorage. Only the Explore map is dark by design; this
// is the switch for everything else.
export const useDarkMode = (): {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
} => {
  const [isDarkMode, setIsDarkMode] = useState(readStored);

  useEffect(() => {
    apply(readStored());
    const onChange = (): void => setIsDarkMode(readStored());
    window.addEventListener(CHANGE_EVENT, onChange);
    return (): void => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);

  const toggleDarkMode = useCallback((): void => {
    const next = !document.documentElement.classList.contains('dark');
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // Private mode or blocked storage: the switch still works for this visit.
    }
    apply(next);
    setIsDarkMode(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return { isDarkMode, toggleDarkMode };
};
